import React from 'react';
import {
  Calculator, Search, Sigma, Split, Type, CalendarDays, Layers, Eraser, Paintbrush, Table2, BarChart3, Cpu,
  Check, ArrowLeft, Rocket, Loader2,
} from 'lucide-react';
import { Button } from '../ui/button';

const ICONS = {
  formulas: Calculator, lookups: Search, sumifs: Sigma, logical: Split, text: Type, datetime: CalendarDays,
  dynamic_arrays: Layers, data_cleaning: Eraser, cond_format: Paintbrush, pivot: Table2, charts: BarChart3, vba_pq: Cpu,
};

const SkillCard = ({ skill, selected, disabled, onToggle }) => {
  const Icon = ICONS[skill.id] || Calculator;
  return (
    <button type="button" onClick={onToggle} disabled={disabled} data-testid={`skill-card-${skill.id}`}
      className={`group relative text-left p-4 rounded-xl border transition-[border-color,background-color,box-shadow,transform] duration-200
        ${selected ? 'border-indigo-500 bg-indigo-50/70 shadow-[0_0_0_3px_rgba(79,70,229,0.12)]' : 'border-slate-200 bg-white hover:border-indigo-300 hover:-translate-y-0.5'}
        ${disabled ? 'opacity-45 cursor-not-allowed hover:translate-y-0' : 'cursor-pointer'}`}>
      <div className="flex items-start justify-between gap-2">
        <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${selected ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 group-hover:bg-indigo-100 group-hover:text-indigo-700'}`}>
          <Icon className="h-4 w-4" />
        </span>
        <span className={`w-5 h-5 rounded-md border flex items-center justify-center ${selected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'}`}>
          {selected && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
        </span>
      </div>
      <p className="mt-3 text-sm font-semibold text-slate-900 leading-snug">{skill.name}</p>
      <p className="mt-1 text-xs text-slate-500 leading-relaxed line-clamp-2">{skill.description}</p>
      <span className={`mt-3 inline-block text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded
        ${skill.mode === 'hands_on' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
        {skill.mode === 'hands_on' ? 'Hands-on spreadsheet' : 'Scenario questions'}
      </span>
    </button>
  );
};

export const SkillPicker = ({ skills, min, max, selected, setSelected, onBack, onStart, starting }) => {
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length < max ? [...s, id] : s));
  const ok = selected.length >= min && selected.length <= max;
  return (
    <div className="space-y-6" data-testid="assessment-skill-step">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Which Excel skills are you confident in?
          </h1>
          <p className="text-slate-600 mt-2 text-sm sm:text-base">{min === max ? `Pick exactly ${max}` : `Pick ${min} to ${max}`}. Your test is built from these — choose honestly; every pick is verified.</p>
        </div>
        <div className={`px-4 py-2 rounded-xl border text-sm font-semibold ${ok ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600'}`}
          data-testid="skill-selected-counter">
          {selected.length} / {max} selected {selected.length < min && `· pick ${min - selected.length} more`}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {skills.map((s) => (
          <SkillCard key={s.id} skill={s} selected={selected.includes(s.id)}
            disabled={!selected.includes(s.id) && selected.length >= max} onToggle={() => toggle(s.id)} />
        ))}
      </div>
      <div className="flex items-center justify-between pt-2">
        <Button variant="ghost" onClick={onBack} className="gap-2 text-slate-600" data-testid="skills-back-btn"><ArrowLeft className="h-4 w-4" /> Back</Button>
        <Button disabled={!ok || starting} onClick={onStart} className="h-11 px-6 gap-2 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="confirm-skills-btn">
          {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />} Start my 20-minute test
        </Button>
      </div>
    </div>
  );
};

export default SkillPicker;
